import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { guidedAPI, GuidedChatMessage } from '../../api/client';
import { ChatMarkdown } from '../ChatMarkdown';
import { CloudRunnerStartingNotice } from './CloudRunnerStartingNotice';
import { RECONNECT, useEventStream } from '../../hooks/useEventStream';
import type { ArtifactTypeValue } from '../../generated/contract';

/**
 * One structured proposal embedded in a copilot reply: a wizard entry, or —
 * beside the project — any artifact by type and place, an edit, or a move.
 */
export interface CopilotSuggestion {
  kind: 'framing' | 'persona' | 'need' | 'requirement' | 'nfr' | 'hazard' | 'artifact' | 'edit' | 'move';
  [key: string]: any;
}

/** Where an applied suggestion goes: the wizard's form, or the project itself. */
export type ApplyTarget = 'wizard' | 'project';

interface GuidedChatPanelProps {
  sessionId: string;
  /**
   * 1-based current wizard step, included in every turn's context. Omitted
   * outside the wizard — the notes panel has no step.
   */
  step?: number;
  /** Snapshot of everything entered in the wizard so far. */
  getState?: () => Record<string, any>;
  /**
   * The artifact the reader has open, when this panel sits in an artifact's
   * notes. Sent with each turn so the assistant answers about it.
   */
  artifactId?: string;
  /** Line under the title; the wizard and the notes panel say different things. */
  subtitle?: string;
  /**
   * Fill the parent instead of standing as its own sticky column. The wizard
   * places this panel beside the form at a fixed width; the notes panel hosts
   * it inside a column the reader can resize.
   */
  embedded?: boolean;
  /**
   * "<messageId>:<segmentIndex>" keys of suggestions already applied. Owned
   * by the wizard and persisted in the session answers, so the applied state
   * survives navigating away and remounting this panel.
   */
  applied?: Record<string, boolean>;
  /**
   * Insert or replace a batch of suggestions in the wizard, applied in order
   * against one snapshot. Each item carries its suggestion key so the wizard
   * can record (and persist) what was applied. Returns one result per
   * suggestion: null on success, or a reason it could not be applied.
   */
  onApplySuggestions?: (
    items: { suggestion: CopilotSuggestion; key: string }[]
  ) => (string | null)[] | Promise<(string | null)[]>;
  /**
   * Where a wizard-shaped suggestion goes, so its button says so: "Add to
   * wizard" beside the form, "Add to project" beside the project. A card
   * that changes the project (a new artifact by place, an edit, a move)
   * says so in both.
   */
  applyTarget?: ApplyTarget;
}

type Segment =
  | { type: 'text'; text: string }
  | { type: 'suggestion'; suggestion: CopilotSuggestion | null; raw: string };

/** Split a copilot reply into prose and ```openv-suggestion fenced JSON blocks. */
const parseSegments = (content: string): Segment[] => {
  const segments: Segment[] = [];
  const re = /```openv-suggestion\s*([\s\S]*?)```/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(content)) !== null) {
    if (match.index > last) {
      segments.push({ type: 'text', text: content.slice(last, match.index) });
    }
    let suggestion: CopilotSuggestion | null = null;
    try {
      const parsed = JSON.parse(match[1].trim());
      if (parsed && typeof parsed === 'object' && typeof parsed.kind === 'string') {
        suggestion = parsed as CopilotSuggestion;
      }
    } catch {
      // malformed block — shown as plain text below
    }
    segments.push({ type: 'suggestion', suggestion, raw: match[1].trim() });
    last = match.index + match[0].length;
  }
  if (last < content.length) {
    segments.push({ type: 'text', text: content.slice(last) });
  }
  return segments;
};

const KIND_LABELS: Record<string, string> = {
  framing: 'Product framing',
  persona: 'Persona',
  need: 'User need',
  requirement: 'Requirement',
  nfr: 'NFR',
  hazard: 'Hazard',
  artifact: 'New artifact',
  edit: 'Change',
  move: 'Move',
};

/**
 * A kind this panel can show and apply: one KIND_LABELS names, as its own
 * key, so that a kind such as "constructor" is not found on the prototype.
 * Any other kind is shown read-only (#379, bug 212).
 */
const isKnownKind = (kind: string): boolean => Object.prototype.hasOwnProperty.call(KIND_LABELS, kind);

/** A few words for a card of an unknown kind, in place of its JSON: its first text field. */
const unknownSummary = (s: CopilotSuggestion): string => {
  const preferred = ['title', 'text', 'name', 'summary', 'description'].map((k) => s[k]);
  const others = Object.entries(s).filter(([k]) => k !== 'kind').map(([, v]) => v);
  const text = String([...preferred, ...others].find((v) => typeof v === 'string' && v.trim()) ?? '').trim();
  return text.length > 80 ? `${text.slice(0, 79)}…` : text;
};

/** The type catalogue's labels, for a new-artifact card. */
const TYPE_LABELS: Record<string, string> = {
  heading: 'Heading',
  description: 'Description',
  persona: 'Persona',
  'user-need': 'User need',
  requirement: 'Requirement',
  'design-item': 'Design item',
  'test-case': 'Test case',
  hazard: 'Hazard',
  other: 'Artifact',
} satisfies Record<ArtifactTypeValue, string>;

/** Where a move card says the artifact is going. */
const moveDestination = (s: CopilotSuggestion): string => {
  const parts: string[] = [];
  if (s.parent !== undefined) parts.push(String(s.parent || '').trim() ? `under ${String(s.parent).trim()}` : 'to the top level');
  if (s.before) parts.push(`before ${String(s.before).trim()}`);
  if (s.after) parts.push(`after ${String(s.after).trim()}`);
  if (s.position) parts.push(String(s.position) === 'first' ? 'first' : 'last');
  return parts.join(', ');
};

/** What an edit card changes, named field by field. */
const editSummary = (s: CopilotSuggestion): string => {
  const parts: string[] = [];
  if (s.title !== undefined) parts.push(`title → ${String(s.title)}`);
  if (s.body !== undefined) parts.push('body rewritten');
  if (s.attributes && typeof s.attributes === 'object') {
    const keys = Object.keys(s.attributes);
    if (keys.length) parts.push(`${keys.join(', ')} changed`);
  }
  return parts.join(' · ');
};

const FRAMING_FIELD_LABELS: Record<string, string> = {
  vision: 'Vision',
  problem_statement: 'Problem statement',
  target_users: 'Target users',
};

// Canned assistant commands, sent as ordinary chat messages so they read
// naturally in the transcript and the reply flows through the normal turn.
type QuickAction = { label: string; title: string; message: string };

const QUICK_ACTIONS: QuickAction[] = [
  {
    label: 'Review step',
    title: 'Quality-check the entries on the current step',
    message:
      'Review this step: quality-check each entry I have filled in — clarity, specificity, testability — and call out the weak ones with concrete improvements (use replace suggestions where useful).',
  },
  {
    label: 'Gap analysis',
    title: 'What is missing, here and across the definition',
    message:
      'Do a gap analysis: given everything entered so far, what is missing on this step and across the definition as a whole? Propose the most important missing entries as suggestions.',
  },
  {
    label: 'Conflict check',
    title: 'Find contradictions, duplicates and inconsistencies',
    message:
      'Check for conflicts: contradictions, duplicates, or inconsistencies anywhere in what I have entered so far. List each one, say which entries clash, and how you would resolve it.',
  },
  {
    label: 'Draft for me',
    title: 'Propose entries for the current step',
    message:
      'Draft entries for this step based on everything you know about the product so far, as suggestion blocks I can add or apply.',
  },
];

// Beside an artifact there is no step to review and nothing to draft into,
// so the notes panel offers what a reader of one artifact actually wants.
const NOTES_QUICK_ACTIONS: QuickAction[] = [
  {
    label: 'Review this',
    title: 'Quality-check the artifact on screen',
    message:
      'Review the artifact I am reading: is it clear, specific and testable? Quote the wording that weakens it and say how you would rewrite it.',
  },
  {
    label: 'How would I verify it?',
    title: 'Propose verification for this artifact',
    message:
      'How would I verify the artifact I am reading? Propose a verification method and the test cases that would demonstrate it, and say what evidence each would produce.',
  },
  {
    label: 'What is missing?',
    title: 'Gaps around this artifact',
    message:
      'What is missing around the artifact I am reading — unstated hazards, absent non-functional requirements, needs or requirements it should trace to but does not?',
  },
];

const suggestionSummary = (s: CopilotSuggestion): { title: string; detail: string } => {
  switch (s.kind) {
    case 'framing':
      return { title: FRAMING_FIELD_LABELS[s.field] || String(s.field || ''), detail: s.text || '' };
    case 'persona':
      return { title: s.name || 'Unnamed persona', detail: s.role || '' };
    case 'need':
      return {
        title: `I need ${s.capability || '…'}`,
        detail: `${s.persona ? `As ${s.persona}, ` : ''}so that ${s.outcome || '…'}`,
      };
    case 'requirement':
      return { title: s.text || '', detail: s.fit_criterion ? `Fit: ${s.fit_criterion}` : '' };
    case 'nfr':
      return { title: `[${s.category || '?'}] ${s.text || ''}`, detail: s.fit_criterion ? `Fit: ${s.fit_criterion}` : '' };
    case 'hazard':
      return { title: s.hazard || '', detail: `${s.harm || ''}${s.severity ? ` (${s.severity})` : ''}` };
    case 'artifact':
      return {
        title: `${TYPE_LABELS[String(s.type)] || String(s.type || '')}: ${s.title || ''}`,
        detail: s.parent ? `Under ${String(s.parent).trim()}${s.after ? `, after ${String(s.after).trim()}` : ''}` : 'At the top level',
      };
    case 'edit':
      return { title: String(s.ref || ''), detail: editSummary(s) };
    case 'move':
      return { title: String(s.ref || ''), detail: moveDestination(s) };
    default:
      // An unknown kind: the card's heading names the kind, this says what it is.
      return { title: unknownSummary(s), detail: '' };
  }
};

/** The button and done labels for one card, for the target it applies to. */
const applyLabels = (
  s: CopilotSuggestion,
  target: ApplyTarget,
  isReplace: boolean
): { button: string; done: string } => {
  // A change to the project says so wherever the card is shown: the wizard
  // sits on top of a project too, and its locked entries are artifacts.
  switch (s.kind) {
    case 'edit':
      return { button: 'Apply change', done: '✓ Changed' };
    case 'move':
      return { button: 'Move', done: '✓ Moved' };
    case 'artifact':
      return { button: '+ Add to project', done: '✓ Added to project' };
    default:
      break;
  }
  if (target === 'project') {
    if (s.kind === 'framing') return { button: 'Apply to project', done: '✓ Applied' };
    return { button: '+ Add to project', done: '✓ Added to project' };
  }
  if (isReplace) return { button: 'Replace in wizard', done: '✓ Replaced in wizard' };
  if (s.kind === 'framing') return { button: 'Apply to wizard', done: '✓ Applied to wizard' };
  return { button: '+ Add to wizard', done: '✓ Added to wizard' };
};

// Floor between two nudges sent from this panel. Wizard saves can come in
// bursts (Next, Next, Skip); the server coalesces what still overlaps a turn.
const NUDGE_MIN_INTERVAL_MS = 1000;

/**
 * One wizard nudge waiting for the throttle window to open. The state is
 * snapshotted when the user acted, so the copilot comments on what they had
 * entered at the moment of the action it is told about.
 */
type DeferredNudge = { step: number; event: string; state: Record<string, any> };

export interface GuidedChatPanelHandle {
  /**
   * Fire a copilot turn reacting to a wizard action (step saved/skipped).
   * Sent immediately — showing the thinking indicator at once — unless it
   * lands inside the one-per-second window, in which case it waits (as the
   * newest held nudge) for the window to open.
   */
  nudge: (step: number, event: string) => void;
}

// The V&V Assistant's chat beside the guided wizard: streams the session
// conversation over SSE and renders its structured proposals as one-click Adds.
const NO_STATE = (): Record<string, any> => ({});

export const GuidedChatPanel = forwardRef<GuidedChatPanelHandle, GuidedChatPanelProps>(({
  sessionId,
  step = 0,
  getState = NO_STATE,
  artifactId,
  subtitle = 'Asks questions and suggests entries you can add with one click.',
  embedded = false,
  applied = {},
  onApplySuggestions,
  applyTarget = 'wizard',
}, ref) => {
  // Suggestions are applied into wizard entry sections. Beside an artifact
  // there is nowhere to put them, so the cards render without their buttons
  // rather than offering an action that cannot work.
  const canApply = !!onApplySuggestions;
  const quickActions = artifactId ? NOTES_QUICK_ACTIONS : QUICK_ACTIONS;
  const [messages, setMessages] = useState<GuidedChatMessage[]>([]);
  const [composerText, setComposerText] = useState('');
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  // The reply as it is being written, pushed by the server as
  // `assistant_partial` while the run is in flight. It is the whole text so
  // far (never a delta), so a dropped event only costs a frame; the final
  // `message` replaces it.
  const [partial, setPartial] = useState('');
  const [sendError, setSendError] = useState('');
  // True when the API reports no runner online: turns queue unanswered, so
  // the panel shows connect instructions instead of a thinking indicator.
  const [runnerOffline, setRunnerOffline] = useState(false);
  // A cloud runner OpenV leased for this turn is coming up: shown instead of
  // the "not connected" notice until the reply arrives.
  const [runnerStarting, setRunnerStarting] = useState(false);
  // Which suggestions were applied lives in `applied` (persisted by the
  // wizard in the session answers) — NOT local state, so a remount cannot
  // re-arm Apply buttons and duplicate entries.
  //
  // A card on its way there is pending from the click on, so a second click
  // before the first one's answer (a double-click, an Apply all) does nothing
  // instead of applying it again (#379, bug 119). The ref is what a click
  // reads, since it changes before React renders; the state disables the
  // button.
  const applyingRef = useRef<Set<string>>(new Set());
  const [applying, setApplying] = useState<ReadonlySet<string>>(() => new Set());

  // The session whose transcript has loaded (or failed to), and whether it
  // was empty: its stream opens then, and an empty one is kicked off. Null
  // again when the session changes, until the next transcript has loaded.
  const [loaded, setLoaded] = useState<{ sessionId: string; empty: boolean } | null>(null);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const kickedRef = useRef(false);
  const lastNudgeRef = useRef(0);
  // The newest nudge that arrived inside the throttle window, waiting for it
  // to open, and the timer that will send it.
  const deferredNudgeRef = useRef<DeferredNudge | null>(null);
  const nudgeTimerRef = useRef<number | null>(null);

  // Snapshot getters live in refs so the SSE effect doesn't resubscribe on
  // every wizard keystroke.
  const getStateRef = useRef(getState);
  getStateRef.current = getState;
  const stepRef = useRef(step);
  stepRef.current = step;
  const artifactRef = useRef(artifactId);
  artifactRef.current = artifactId;
  // The session on screen now, for a send that outlives a session change.
  const sessionRef = useRef(sessionId);
  sessionRef.current = sessionId;

  const appendMessage = useCallback((msg: GuidedChatMessage) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === msg.id)) return prev;
      return [...prev, msg];
    });
    if (msg.role === 'assistant' || msg.role === 'system') {
      setTyping(false);
      // The real message has landed (or the turn failed): whatever the
      // streaming bubble was showing is superseded.
      setPartial('');
    }
    // An assistant reply proves a runner is processing turns again.
    if (msg.role === 'assistant') {
      setRunnerOffline(false);
      setRunnerStarting(false);
    }
  }, []);

  // Digest a kickoff/nudge/send response: runner offline wins over any
  // status; otherwise "launched"/"pending" mean a reply is on its way.
  const applyTurnStatus = useCallback((data?: { status?: string; runner_online?: boolean; runner_starting?: boolean }) => {
    if (!data) return;
    const starting = data.runner_online === false && Boolean(data.runner_starting);
    setRunnerStarting(starting);
    if (data.runner_online === false && !starting) {
      setRunnerOffline(true);
      setTyping(false);
      return;
    }
    setRunnerOffline(false);
    if (data.status === 'launched' || data.status === 'pending') {
      setTyping(true);
    } else if (data.status === 'unavailable') {
      setTyping(false);
    }
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    kickedRef.current = false;
    setMessages([]);
    setPartial('');
    // Nothing the previous session said carries over (#379, bug 213).
    setSendError('');
    setTyping(false);
    setRunnerOffline(false);
    let cancelled = false;
    (async () => {
      try {
        const res = await guidedAPI.listMessages(sessionId);
        if (cancelled) return;
        const transcript = res.data || [];
        transcript.forEach((m) => appendMessage(m));
        // Opens the stream below, then kicks off an empty conversation.
        setLoaded({ sessionId, empty: transcript.length === 0 });
      } catch {
        if (cancelled) return;
        setSendError('Failed to load the assistant conversation.');
        // The stream opens all the same, so replies still arrive (#379, bug
        // 210); a transcript that could not be read is not an empty one, so
        // it is not kicked off.
        setLoaded({ sessionId, empty: false });
      }
    })();
    return () => {
      cancelled = true;
      setLoaded(null);
    };
  }, [sessionId, appendMessage]);

  // The session's stream, open from the moment its transcript has loaded. A
  // drop retries for ever (RECONNECT.cappedExponent: 2, 4, 8, 15, 15 ... s),
  // an open starting the wait over. A session change closes it at once, and
  // cancels a pending reconnect so that it cannot reopen a session the panel
  // has left (#379, bug 203), as an unmount does.
  useEventStream(
    loaded && loaded.sessionId === sessionId ? guidedAPI.chatStreamUrl(sessionId) : null,
    {
      message: (event) => {
        try {
          const msg = JSON.parse(event.data) as GuidedChatMessage;
          if (msg && msg.id) appendMessage(msg);
        } catch {
          // ignore malformed events
        }
      },
      assistant_partial: (event) => {
        try {
          const data = JSON.parse(event.data) as { run_id?: string; text?: string };
          if (typeof data?.text !== 'string' || !data.text) return;
          setPartial(data.text);
          // Text is arriving, so the assistant is demonstrably answering.
          setTyping(false);
          setRunnerOffline(false);
        } catch {
          // ignore malformed events
        }
      },
    },
    { withCredentials: true, reconnect: RECONNECT.cappedExponent }
  );

  // Empty conversation: ask the assistant to open with a question. Declared
  // after the stream, so that the stream is open before the kickoff goes.
  useEffect(() => {
    if (!loaded || loaded.sessionId !== sessionId || !loaded.empty || kickedRef.current) return;
    kickedRef.current = true;
    (async () => {
      try {
        const kick = await guidedAPI.kickoffChat(
          sessionId,
          stepRef.current,
          getStateRef.current(),
          artifactRef.current
        );
        applyTurnStatus(kick.data);
      } catch {
        // the assistant is optional — the wizard still works without it
      }
    })();
  }, [loaded, sessionId, applyTurnStatus]);

  // Send one nudge now, opening a fresh throttle window.
  const sendNudge = useCallback(
    (nudge: DeferredNudge) => {
      if (!sessionId) return;
      lastNudgeRef.current = Date.now();
      setTyping(true);
      guidedAPI
        .nudgeChat(sessionId, nudge.step, nudge.state, nudge.event)
        .then((res) => applyTurnStatus(res.data))
        .catch(() => setTyping(false));
    },
    [sessionId, applyTurnStatus]
  );

  // Wizard actions (Next/Skip) call this through a ref so the thinking
  // indicator appears the moment the user acts, not when the reply lands.
  useImperativeHandle(
    ref,
    () => ({
      nudge: (nudgeStep: number, event: string) => {
        if (!sessionId) return;
        // The server decides what to do with a nudge that lands mid-turn (it
        // parks the newest one and answers it when the turn finishes), so the
        // panel does not track run state. It only refuses to spam: at most
        // one nudge a second, however fast the wizard is saving.
        //
        // A nudge inside that window is DEFERRED, never dropped: the last
        // save of a burst carries the freshest wizard state, and dropping it
        // would keep the newest state from ever reaching the server (and the
        // server-side coalescing from ever seeing it). The newest one waits
        // in a ref — replacing any held one — and goes out when the window
        // opens.
        const held: DeferredNudge = { step: nudgeStep, event, state: getStateRef.current() };
        const wait = NUDGE_MIN_INTERVAL_MS - (Date.now() - lastNudgeRef.current);
        if (wait <= 0) {
          sendNudge(held);
          return;
        }
        // Deferred only: nothing has been asked of the server yet, so the
        // thinking indicator stays as it is until the request actually goes.
        deferredNudgeRef.current = held;
        if (nudgeTimerRef.current === null) {
          nudgeTimerRef.current = window.setTimeout(() => {
            nudgeTimerRef.current = null;
            const next = deferredNudgeRef.current;
            deferredNudgeRef.current = null;
            if (next) sendNudge(next);
          }, wait);
        }
      },
    }),
    [sessionId, sendNudge]
  );

  // A held nudge belongs to the session it was entered against: drop it (and
  // its timer) when the panel unmounts or moves to another session.
  useEffect(
    () => () => {
      if (nudgeTimerRef.current !== null) {
        window.clearTimeout(nudgeTimerRef.current);
        nudgeTimerRef.current = null;
      }
      deferredNudgeRef.current = null;
    },
    [sessionId]
  );

  // Scroll so the START of the newest message is in view — the reader begins
  // at its top, not its tail.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const last = lastMessageRef.current;
    if (!scroller || !last) return;
    const delta = last.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    scroller.scrollTo({ top: scroller.scrollTop + delta - 4, behavior: 'smooth' });
  }, [messages]);

  // The typing indicator and the streaming bubble sit below the last
  // message; keep the tail in view as the reply grows.
  useEffect(() => {
    if (!typing && !partial) return;
    const scroller = scrollerRef.current;
    if (scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
  }, [typing, partial]);

  // preset carries a quick-action message; without it the composer text is sent.
  const send = async (preset?: string) => {
    const content = (preset ?? composerText).trim();
    if (!sessionId || !content || sending) return;
    setSending(true);
    setSendError('');
    setTyping(true);
    // The answer belongs to the session the message went to: once the panel
    // has moved to another, the echo and the turn status are dropped (#379,
    // bug 217).
    const sentIn = sessionId;
    try {
      const res = await guidedAPI.sendMessage(
        sentIn,
        content,
        stepRef.current,
        getStateRef.current(),
        artifactRef.current
      );
      if (sessionRef.current === sentIn) {
        if (res.data.message) appendMessage(res.data.message);
        const starting = res.data.runner_online === false && Boolean(res.data.runner_starting);
        setRunnerStarting(starting);
        if (res.data.runner_online === false && !starting) {
          setRunnerOffline(true);
          setTyping(false);
        } else {
          setRunnerOffline(false);
        }
      }
      if (!preset) setComposerText('');
    } catch {
      if (sessionRef.current === sentIn) {
        setTyping(false);
        setSendError('Message failed to send — please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  // Apply the cards not already on their way, marking them pending first.
  // Null when there is nothing left to apply.
  const applyOnce = async (
    items: { suggestion: CopilotSuggestion; key: string }[]
  ): Promise<(string | null)[] | null> => {
    const fresh = items.filter((i) => !applyingRef.current.has(i.key));
    if (!onApplySuggestions || fresh.length === 0) return null;
    fresh.forEach((i) => applyingRef.current.add(i.key));
    setApplying(new Set(applyingRef.current));
    try {
      return await onApplySuggestions(fresh);
    } finally {
      fresh.forEach((i) => applyingRef.current.delete(i.key));
      setApplying(new Set(applyingRef.current));
    }
  };

  // Apply every not-yet-applied suggestion of one reply in a single batch.
  // The wizard records and persists which keys applied successfully.
  const applyAll = async (pending: { suggestion: CopilotSuggestion; key: string }[]) => {
    const results = await applyOnce(pending);
    if (!results) return;
    const errors = results.filter((r): r is string => r !== null);
    setSendError(
      errors.length > 0
        ? `${errors.length} suggestion${errors.length > 1 ? 's' : ''} could not be applied — ${errors[0]}`
        : ''
    );
  };

  // Human-readable label for a suggestion's "replaces" target: when it is an
  // entry's stable id, show that entry's title from the wizard state instead
  // of the raw UUID; otherwise show the value as-is (it is already the title).
  const replacesLabel = (s: CopilotSuggestion): string => {
    const target = String(s.replaces || '').trim();
    if (!target) return '';
    try {
      const state = getStateRef.current() || {};
      const lists: Record<string, { items: any[]; title: (e: any) => any }> = {
        persona: { items: state.step_2?.personas, title: (e) => e.name },
        need: { items: state.step_3?.needs, title: (e) => e.capability },
        requirement: { items: state.step_4?.requirements, title: (e) => e.text },
        nfr: { items: state.step_5?.nfrs, title: (e) => e.text },
        hazard: { items: state.step_6?.hazards, title: (e) => e.hazard },
      };
      const cfg = lists[s.kind];
      const items = Array.isArray(cfg?.items) ? cfg.items : [];
      const hit = items.find((e) => e && e.id === target);
      if (hit) {
        const title = String(cfg.title(hit) || '').trim();
        if (title) return title;
      }
    } catch {
      // fall through to the raw value
    }
    return target;
  };

  const renderSuggestion = (seg: Segment & { type: 'suggestion' }, key: string) => {
    if (!seg.suggestion) {
      return (
        <pre key={key} style={{ fontSize: 12, background: 'var(--surface-alt)', padding: 8, borderRadius: 4, overflowX: 'auto' }}>
          {seg.raw}
        </pre>
      );
    }
    const s = seg.suggestion;
    const known = isKnownKind(s.kind);
    const { title, detail } = suggestionSummary(s);
    const isAdded = !!applied[key];
    // A suggestion replaces existing content when it targets an entry
    // ("replaces") or rewrites a framing field that already has text.
    let isReplace = !!s.replaces;
    if (s.kind === 'framing') {
      try {
        const step1 = (getStateRef.current()?.step_1 || {}) as Record<string, any>;
        isReplace = !!String(step1[String(s.field)] || '').trim();
      } catch {
        isReplace = false;
      }
    }
    const { button: buttonLabel, done: doneLabel } = applyLabels(s, applyTarget, isReplace);
    return (
      <div
        key={key}
        style={{
          border: '1px solid var(--tint-blue-border)',
          background: 'var(--tint-blue)',
          borderRadius: 6,
          padding: '8px 10px',
          margin: '6px 0',
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', marginBottom: 3 }}>
          {known ? KIND_LABELS[s.kind] : s.kind}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: detail ? 2 : 6 }}>{title}</div>
        {detail && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>{detail}</div>}
        {s.replaces && (
          <div style={{ fontSize: 12, color: 'var(--neutral)', fontStyle: 'italic', marginBottom: 6 }}>
            Replaces: {replacesLabel(s)}
          </div>
        )}
        {!known ? (
          <div style={{ fontSize: 12, color: 'var(--neutral)', fontStyle: 'italic' }}>
            OpenV cannot add this kind of suggestion.
          </div>
        ) : !canApply ? (
          <div style={{ fontSize: 12, color: 'var(--neutral)', fontStyle: 'italic' }}>
            Open the project to add this.
          </div>
        ) : isAdded ? (
          <span style={{ fontSize: 12, color: 'var(--success)' }}>{doneLabel}</span>
        ) : (
          <button
            className="button-secondary"
            style={{ padding: '5px 10px', fontSize: 12 }}
            disabled={applying.has(key)}
            onClick={async () => {
              const results = await applyOnce([{ suggestion: s, key }]);
              if (results) setSendError(results[0] === null ? '' : results[0]);
            }}
          >
            {buttonLabel}
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      data-help-pane="guided.assistant"
      data-help-key="guided.assistant"
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface)',
        ...(embedded
          ? { width: '100%', height: '100%', minHeight: 0 }
          : {
              width: 340,
              minWidth: 340,
              border: '1px solid var(--border)',
              borderRadius: 4,
              position: 'sticky',
              top: 20,
              height: 'calc(100vh - 160px)',
              minHeight: 420,
            }),
      }}
    >
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: 14 }}>V&amp;V Assistant</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{subtitle}</div>
      </div>

      <div ref={scrollerRef} style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {/* Not while the notice below says no runner can answer (#379, bug 211). */}
        {messages.length === 0 && !typing && !partial && !runnerOffline && (
          <div style={{ textAlign: 'center', color: 'var(--neutral)', fontSize: 12, marginTop: 24 }}>
            The assistant will join in a moment — or ask it anything about your requirements.
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={m.id}
            ref={i === messages.length - 1 ? lastMessageRef : undefined}
            style={{
              display: 'flex',
              justifyContent: m.role === 'user' ? 'flex-end' : m.role === 'system' ? 'center' : 'flex-start',
              marginBottom: 8,
            }}
          >
            {m.role === 'system' ? (
              <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--neutral)', textAlign: 'center' }}>
                {m.content}
              </div>
            ) : (
              <div
                style={{
                  maxWidth: '90%',
                  padding: '8px 12px',
                  borderRadius: 12,
                  fontSize: 13,
                  lineHeight: 1.5,
                  // A person's message is literal text, so their line breaks
                  // are honoured as typed. The assistant's is markdown, which
                  // brings its own block layout — preserving its newlines on
                  // top of that would space every paragraph twice.
                  whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal',
                  wordBreak: 'break-word',
                  background: m.role === 'user' ? 'var(--accent)' : 'var(--surface-alt)',
                  color: m.role === 'user' ? 'var(--accent-fg)' : 'var(--text)',
                  borderBottomRightRadius: m.role === 'user' ? 4 : 12,
                  borderBottomLeftRadius: m.role === 'assistant' ? 4 : 12,
                }}
              >
                {m.role === 'assistant'
                  ? (() => {
                      const segments = parseSegments(m.content);
                      const pending = segments
                        .map((seg, i) => ({ seg, key: `${m.id}:${i}` }))
                        .filter(
                          (x): x is { seg: Segment & { type: 'suggestion' }; key: string } =>
                            x.seg.type === 'suggestion' &&
                            !!x.seg.suggestion &&
                            isKnownKind(x.seg.suggestion.kind) &&
                            !applied[x.key]
                        )
                        .map((x) => ({ suggestion: x.seg.suggestion as CopilotSuggestion, key: x.key }));
                      return (
                        <>
                          {segments.map((seg, i) =>
                            seg.type === 'text' ? (
                              seg.text.trim() ? (
                                <ChatMarkdown key={`${m.id}:${i}`} text={seg.text.trim()} />
                              ) : null
                            ) : (
                              renderSuggestion(seg, `${m.id}:${i}`)
                            )
                          )}
                          {canApply && pending.length >= 2 && (
                            <div style={{ marginTop: 8 }}>
                              <button
                                className="button"
                                onClick={() => applyAll(pending)}
                                style={{
                                  background: 'var(--accent)',
                                  padding: '5px 12px',
                                  fontSize: 12,
                                  width: 'auto',
                                }}
                              >
                                Apply all ({pending.length})
                              </button>
                            </div>
                          )}
                        </>
                      );
                    })()
                  : m.content}
              </div>
            )}
          </div>
        ))}
        {runnerOffline && (
          <div
            style={{
              border: '1px solid var(--tint-yellow-border)',
              background: 'var(--tint-yellow)',
              borderRadius: 6,
              padding: '10px 12px',
              marginBottom: 8,
              fontSize: 12,
              color: 'var(--warning-text)',
              lineHeight: 1.5,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ V&amp;V Assistant not connected</div>
            No runner is online to answer, so assistant replies are paused. To connect one, open{' '}
            <Link to="/org/settings" style={{ color: 'var(--warning-text)', fontWeight: 600 }}>
              Workspace Settings → Runners
            </Link>{' '}
            and launch the Agent Connector on your machine — or restart it if it was already running.
            Messages you send are saved and will be answered as soon as a runner connects.
          </div>
        )}
        {runnerStarting && !runnerOffline && !partial && <CloudRunnerStartingNotice />}
        {partial && (
          <div data-testid="assistant-partial" style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
            <div
              style={{
                maxWidth: '90%',
                padding: '8px 12px',
                borderRadius: 12,
                borderBottomLeftRadius: 4,
                fontSize: 13,
                lineHeight: 1.5,
                wordBreak: 'break-word',
                background: 'var(--surface-alt)',
                color: 'var(--text)',
              }}
            >
              {/* Suggestion blocks are only rendered once the reply is
                  complete — half a fenced JSON block is not a card. The prose
                  around them is still markdown while it arrives, so the text
                  does not visibly restyle itself the moment the reply ends. */}
              <ChatMarkdown text={partial} streaming />
            </div>
          </div>
        )}
        {typing && !partial && !runnerOffline && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
            <div
              style={{
                padding: '8px 12px',
                borderRadius: 12,
                fontSize: 12,
                fontStyle: 'italic',
                background: 'var(--surface-alt)',
                color: 'var(--text-muted)',
              }}
            >
              The assistant is thinking…
            </div>
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--border)', padding: 10 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {quickActions.map((qa) => (
            <button
              key={qa.label}
              onClick={() => send(qa.message)}
              disabled={sending}
              title={qa.title}
              style={{
                background: 'var(--tint-blue)',
                border: '1px solid var(--tint-blue-border)',
                color: 'var(--accent-strong)',
                borderRadius: 12,
                padding: '5px 10px',
                fontSize: 12,
                cursor: 'pointer',
                width: 'auto',
                whiteSpace: 'nowrap',
                opacity: sending ? 0.5 : 1,
              }}
            >
              {qa.label}
            </button>
          ))}
        </div>
        {sendError && <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 6 }}>{sendError}</div>}
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
          <textarea
            value={composerText}
            onChange={(e) => setComposerText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask the V&amp;V Assistant… (Ctrl+Enter to send)"
            enterKeyHint="enter"
            rows={2}
            style={{ flex: 1, minHeight: 40, resize: 'none', fontSize: 13 }}
          />
          <button
            className="button"
            onClick={() => send()}
            disabled={sending || !composerText.trim()}
            style={{
              background: 'var(--accent)',
              opacity: sending || !composerText.trim() ? 0.5 : 1,
              whiteSpace: 'nowrap',
              padding: '8px 12px',
              fontSize: 13,
            }}
          >
            {sending ? '…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
});

GuidedChatPanel.displayName = 'GuidedChatPanel';
